import React, { useEffect } from 'react';
import { useDraggable } from '@/hooks/useDraggable';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { useCollapsible, PanelProvider } from '@/hooks/useCollapsible';
import { useMobile } from '@/hooks/useMobile';
import { cn } from '@/lib/utils';

interface DraggableDialogProps {
  isOpen?: boolean;
  onClose: () => void;
  title: string | null;
  subtitle?: string;
  icon?: React.ElementType;
  children: React.ReactNode;
  initialPosition?: { x: number; y: number };
  width?: number;
  panelId: string;
}

export const DraggableDialog: React.FC<DraggableDialogProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: TitleIcon,
  children,
  initialPosition,
  width = 384,
  panelId,
}) => {
  const { isCollapsed, setIsCollapsed } = useCollapsible(panelId);
  const { isMobile } = useMobile();

  const { elementRef, handleMouseDown, isDragging, setPosition } = useDraggable(
    {
      initialPosition,
      disabled: isMobile,
    },
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!isMobile) {
      const pos = initialPosition || {
        x: window.innerWidth - width - 50,
        y: 50,
      };
      setPosition(pos);
    }
    setIsCollapsed(false);
  }, [isOpen, initialPosition, width, setPosition, isMobile]);

  if (!isOpen) return null;

  return (
    <PanelProvider value={panelId}>
      <div
        ref={elementRef}
        className={cn(
          'fixed z-50 flex flex-col overflow-hidden shadow-2xl backdrop-blur-md',
          'bg-background/95 border-border',
          isMobile
            ? 'bottom-0 left-0 right-0 w-full rounded-t-xl border-t'
            : 'rounded-xl border',
        )}
        style={
          isMobile
            ? {
                transform: 'none',
                top: 'auto',
                bottom: 0,
                left: 0,
                width: '100%',
                maxHeight: '85vh',
              }
            : {
                width: `${width}px`,
                top: 0,
                left: 0,
                maxHeight: '85vh',
                cursor: isDragging ? 'grabbing' : 'default',
              }
        }
      >
        {/* ── Header — always visible, never scrolls away ── */}
        <header
          className={cn(
            'flex-shrink-0 border-b border-border bg-muted/40 p-4',
            !isMobile && 'cursor-grab active:cursor-grabbing',
          )}
          onMouseDown={!isMobile ? handleMouseDown : undefined}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="mb-1 flex items-center gap-2">
                {TitleIcon && (
                  <TitleIcon size={16} className="text-muted-foreground" />
                )}
                <h2 className="truncate text-lg font-bold text-foreground">
                  {title}
                </h2>
              </div>
              {subtitle && (
                <p className="truncate text-xs text-muted-foreground">
                  {subtitle}
                </p>
              )}
            </div>
            <div className="ml-3 flex items-center gap-2">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="cursor-pointer rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              >
                {isCollapsed ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>
              <button
                onClick={onClose}
                className="cursor-pointer rounded-full p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </header>

        {/* ── Content ── */}
        <div
          className={cn(
            'transition-all duration-200',
            isCollapsed
              ? 'max-h-0 overflow-hidden'
              : 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden',
          )}
        >
          {children}
        </div>
      </div>
    </PanelProvider>
  );
};
